import { Box, Button, Stack } from '@mui/material'
import { CarrierVisitDirection, OrderStatus } from '@planning/app/api'
import { SearchContainerViewStore } from '@planning/rt-stores/tallyman/SearchContainerViewStore'
import { useTranslate } from '@tolgee/react'
import { ContainerMobile, Header, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo } from 'react'
import { ContainerSearchResult } from './Components/ContainerSearchResult'
import { CustomAlert } from './Components/CustomAlert'
import { NavigationStackBackButton } from './Components/HeaderComponents/NavigationStackBackButton'

interface IProps {
  store: SearchContainerViewStore
  onContainerCardClick: (id: number) => void
  onReportClick: () => void
  operationStoppedComponent?: React.ReactNode
  tallymanOperationsUIStore: any
  onSearchInRobClick: () => void
}

export const SearchContainers: FC<IProps> = observer(
  ({
    store,
    onContainerCardClick,
    onReportClick,
    onSearchInRobClick,
    operationStoppedComponent,
    tallymanOperationsUIStore,
  }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    useEffect(() => {
      window.scrollTo(0, 0)
    }, [])

    const StartCheckAction = useMemo(() => {
      const Component: FC<{ direction: CarrierVisitDirection; disabled?: boolean }> = ({
        direction,
        disabled = false,
      }) => (
        <Button
          data-cy='start-check-in'
          variant='contained'
          size='large'
          sx={{ width: '100%', mt: '1rem' }}
          disabled={disabled || !store.parentStore.isSelectedVisitInOperation}
        >
          {direction === 'Inbound'
            ? t('startCheckIn', 'Start check in')
            : t('startCheckOut', 'Start check out')}
        </Button>
      )
      return Component
    }, [store.parentStore.isSelectedVisitInOperation, t])

    return (
      <>
        <Header
          filterTestId='search-container-autocomplete'
          leftRenderOption={<NavigationStackBackButton />}
          enableSearchField
          onSearchFieldChange={store.setSearchFilter}
          searchFilter={store.searchFilter}
        />

        <Box
          style={{
            backgroundColor: theme.palette.grey[200],
            minHeight: 'calc(100vh - 65px)',
          }}
        >
          <ContainerMobile sx={{ pt: theme.customSpacing.l }}>
            <Stack gap={theme.customSpacing.m}>
              {!store.isSearchFilterValid && (
                <CustomAlert
                  message={t(
                    'hintTryToTypeAtLeast5CharactersOfTheContainerNumber',
                    'Hint, try to type at least 5 characters of the container number',
                  )}
                  severity='info'
                />
              )}
              {!!store.searchFilter &&
                store.filteredOrders?.map(o => {
                  const isEffectivelyApproved = o.data.isApproved ?? false

                  return (
                    <ContainerSearchResult
                      key={`container-search-result-${o.data.containerNumber}`}
                      containerNumber={o.data.containerNumber ?? ''}
                      status={o.data.status}
                      order={o.data}
                      highlight={store.searchFilter}
                      direction={o.data.direction}
                      isOnTerminal={
                        ((o.container?.data.isOnTerminal || o.data.isOnTerminal) &&
                          o.containerJourney?.inboundOrderStatus === OrderStatus.Fulfilled) ??
                        false
                      }
                      onClick={() => {
                        if (store.parentStore.isSelectedVisitInOperation) onContainerCardClick(o.id)
                      }}
                      plannedYardLocation={o.data.plannedYardLocation ?? undefined}
                      isAssignedCraneNotToSelected={o.isAssignedCraneNotToSelected}
                      assignedCraneName={o.assignedCraneName}
                      containerJourneyCategory={o.journeyCategory}
                      noPlannedEquipment={tallymanOperationsUIStore.items.length === 0}
                      isApproved={isEffectivelyApproved}
                      releaseEnd={o?.data?.releaseValidityDate}
                    >
                      <StartCheckAction direction={o.data.direction} />
                    </ContainerSearchResult>
                  )
                })}

              {store.parentStore.selectedOperation === 'Load' &&
                store.isSearchFilterValid &&
                !store.filteredOrders?.length && (
                  <>
                    <CustomAlert
                      severity='warning'
                      message={t(
                        'noMatchingContainerForLoading',
                        'No matching container for loading',
                      )}
                    ></CustomAlert>
                  </>
                )}

              {store.parentStore.selectedOperation === 'Discharge' &&
                store.isSearchFilterValid &&
                !store.filteredOrders?.length && (
                  <>
                    <CustomAlert
                      severity='warning'
                      action={
                        <>
                          {/* TODO: enable, when workflow is defined by the product */}
                          {/*  <Button color='inherit' onClick={onReportClick}>
                      {t('report', 'Report')}
                    </Button> */}
                        </>
                      }
                      message={t(
                        'noMatchingContainerForDischarge',
                        'No matching container for discharge',
                      )}
                    ></CustomAlert>
                    <Button
                      data-cy='search-in-rob'
                      variant='contained'
                      size='large'
                      sx={{ width: '100%', mt: '1rem' }}
                      onClick={onSearchInRobClick}
                    >
                      {t('searchInRob', 'Search in ROB')}
                    </Button>
                  </>
                )}

              {operationStoppedComponent}
            </Stack>
          </ContainerMobile>
        </Box>
      </>
    )
  },
)
